import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Sort,
  Inject,
  SortSettingsModel,
  PageSettingsModel,
  RowSelectEventArgs,
} from '@syncfusion/ej2-react-grids';
import { Refresh } from '@mui/icons-material';

import { RefundView } from '../../../../../services/RefundService/types/refundTypes';
import { DocumentView } from '../../../../../services/RefundService/types/documentTypes';
import { transformDocumentsToGridData, DocumentGridRow } from './hooks';
import {
  IconTemplate,
  FileNameTemplate,
  UploadedByTemplate,
  UploadDateTemplate,
} from './templates';

export interface DocumentsGridProps {
  refund: RefundView;
  documents: DocumentView[];
  isLoading: boolean;
  error?: any;
  onDocumentView: (document: DocumentView) => void;
  onRefresh: () => void;
  className?: string;
  testId?: string;
}

// Grid settings
const sortSettings: SortSettingsModel = {
  columns: [{ field: 'uploadDate', direction: 'Descending' }],
};

const pageSettings: PageSettingsModel = {
  pageSize: 12,
  pageSizes: [8, 12, 20, 30],
  currentPage: 1,
};

/**
 * DocumentsGrid - Displays refund documents with view functionality
 * 
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Utility functions for data transformation
 * - Clean component structure
 */
export const DocumentsGrid: React.FC<DocumentsGridProps> = ({
  refund,
  documents,
  isLoading,
  error,
  onDocumentView,
  onRefresh,
  className,
  testId,
}) => {
  const documentsArray = documents || [];

  // Transform documents to grid data
  const gridData = useMemo(
    () => transformDocumentsToGridData(documentsArray),
    [documentsArray]
  );

  // Handle row selection
  const handleRowSelected = useCallback(
    (args: RowSelectEventArgs) => {
      const selectedRow = args.data as DocumentGridRow;
      onDocumentView(selectedRow.document);
    },
    [onDocumentView]
  );

  // Create templates
  const iconTemplate = useMemo(
    () => (props: DocumentGridRow) => <IconTemplate iconEmoji={props.iconEmoji} />,
    []
  );

  const fileNameTemplate = useMemo(
    () => (props: DocumentGridRow) => (
      <FileNameTemplate fileName={props.fileName} fileType={props.fileType} />
    ),
    []
  );

  const uploadedByTemplate = useMemo(
    () => (props: DocumentGridRow) => <UploadedByTemplate uploadBy={props.uploadBy} />,
    []
  );

  const uploadDateTemplate = useMemo(
    () => (props: DocumentGridRow) => (
      <UploadDateTemplate uploadDateFormatted={props.uploadDateFormatted} />
    ),
    []
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          width: '800px',
          maxWidth: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 160,
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <CircularProgress size={32} />
        <Typography variant="body2">Loading documents...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '800px', maxWidth: '100%' }}>
        <Stack spacing={1.5}>
          <Alert severity="error" sx={{ fontSize: '0.875rem' }}>
            Failed to load documents: {error?.data?.message || 'An error occurred'}
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Tooltip title="Retry loading documents">
              <IconButton onClick={onRefresh} color="primary" size="small">
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '800px', maxWidth: '100%' }}>
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          {gridData.length} document{gridData.length !== 1 ? 's' : ''} found
        </Typography>
        <Tooltip title="Refresh documents">
          <IconButton onClick={onRefresh} size="small" sx={{ p: 0.5 }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box className={className}>
        <GridComponent
          dataSource={gridData}
          allowPaging={true}
          allowSorting={true}
          pageSettings={pageSettings}
          sortSettings={sortSettings}
          rowSelected={handleRowSelected}
          height="320"
          width="800"
          style={{
            borderRadius: '6px',
            overflow: 'hidden',
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          }}
          data-testid={testId}
        >
          <ColumnsDirective>
            <ColumnDirective
              headerText=""
              width="50"
              textAlign="Center"
              allowSorting={false}
              allowFiltering={false}
              template={iconTemplate}
            />
            <ColumnDirective
              field="fileName"
              headerText="File Name"
              width="320"
              allowSorting={true}
              allowFiltering={true}
              template={fileNameTemplate}
            />
            <ColumnDirective
              field="uploadBy"
              headerText="Uploaded By"
              width="180"
              textAlign="Left"
              allowSorting={true}
              allowFiltering={true}
              template={uploadedByTemplate}
            />
            <ColumnDirective
              field="uploadDateFormatted"
              headerText="Upload Date"
              width="200"
              textAlign="Left"
              allowSorting={true}
              allowFiltering={true}
              template={uploadDateTemplate}
            />
            <ColumnDirective
              field="uploadDate"
              headerText="Date"
              width="50"
              type="date"
              format="yMd"
              textAlign="Center"
              allowSorting={true}
              allowFiltering={true}
              visible={false}
            />
          </ColumnsDirective>
          <Inject services={[Page, Sort]} />
        </GridComponent>
      </Box>
    </Box>
  );
};

export default DocumentsGrid;
